import React, { useEffect } from "react";
import { Platform, Pressable, ScrollView, Text, View } from "react-native";
import { Ionicons } from "@expo/vector-icons";
import moment from "moment-timezone/builds/moment-timezone-with-data-10-year-range.js";
import tw from "twrnc";

import useWorkspaceAuth from "@/components/teams/useWorkspaceAuth";
import { brandColor } from "@/components/globalStyle";
import { formatEventDate, eventTitle } from "@/components/globalUtils";
import { useIsMobileUX } from "@/components/hooks/useIsMobileUX";
import useSubscribeToEventUpdates from "@/components/hooks/useSubscribeToEventUpdates";
import RoomSwitcher from "@/components/rooms/RoomSwitcher";
import { isGrouped, isRoom } from "@/components/rooms/rooms";
import useEventMenu from "@/components/screens/Events/useEventMenu";
import { LiveIndicator, TestIndicator } from "@/components/shared/Buttons";
import SupportButton from "@/components/support/SupportButton";
import { t } from "@/localization";

// EventEdit itself, shown as the event's overview: cover, link, Go Live, buy.
const OVERVIEW_ITEM = {
  title: t("Overview"),
  icon: "home-outline",
  screen: "EventEdit",
};

const formatDateLine = ({
  days = 1,
  event_datetime_utc,
  event_timezone,
  event_window_end_datetime_utc,
}) => {
  const start = formatEventDate(event_datetime_utc, event_timezone);
  if (!start) return null;
  // Unpublished multi-day events may have no end yet.
  const end = formatEventDate(event_window_end_datetime_utc, event_timezone);
  const dates =
    days <= 1
      ? start
      : end
      ? t("{days}-Day Event: {start} – {end}", { days, start, end })
      : t("{days}-Day Event: {start}", { days, start });
  return `${dates}${
    moment.tz.guess() !== event_timezone ? ` (${event_timezone})` : ""
  }`;
};

// The event menu on wide layouts: the same items as the list under the event on
// phones (useEventMenu), plus Overview, with the open one highlighted.
const EventSidebar = ({
  slug,
  initialEvent,
  activeSection,
  navigation,
  onOpen,
  onSwitchRoom,
  onEventLoaded,
  onClose,
  style,
}) => {
  // The account the event is in: yours, or the one you're working in (Teams).
  const { broadcaster } = useWorkspaceAuth();
  const routeCopy =
    initialEvent?.slug === slug && initialEvent.title ? initialEvent : null;
  const { event: loaded } = useSubscribeToEventUpdates(
    routeCopy || { broadcaster, slug }
  );
  // Switching rooms keeps the menu mounted with a new slug: until that room has
  // loaded, show the copy of it that came with the route.
  const event =
    loaded?.slug === slug ? loaded : routeCopy || { broadcaster, slug };

  useEffect(() => {
    if (event.id) onEventLoaded?.(event);
  }, [event.id, event.is_test, onEventLoaded]);
  const {
    menu,
    modals,
    groupPrimary,
    groupRooms,
    roomsAddable,
    roomsRenamable,
    onAddRoomPress,
  } = useEventMenu({
    event,
    slug,
    navigation,
    switchRoom: onSwitchRoom,
    openScreen: (screen) => onOpen(screen, event),
  });

  // Same rule as the screen headers: desktop web has the floating chat launcher.
  const isMobileUX = useIsMobileUX();
  const showSupportButton = Platform.OS !== "web" || isMobileUX;
  const dateLine = !event.is_test && formatDateLine(event);
  const items = [OVERVIEW_ITEM, ...menu];

  return (
    <View style={[tw`flex-1`, style]}>
      <View style={tw`flex-row items-center justify-between px-3 pt-3 pb-1`}>
        <Pressable
          onPress={onClose}
          accessibilityRole="button"
          accessibilityLabel={t("Close event")}
          hitSlop={8}
          style={({ hovered, pressed }) => [
            tw`rounded-full p-1`,
            (hovered || pressed) && tw`bg-gray-200`,
          ]}
        >
          <Ionicons name="close-outline" size={28} color={brandColor} />
        </Pressable>
        {showSupportButton && <SupportButton style={tw`mr-2`} />}
      </View>

      <View style={tw`px-5 pb-3`}>
        <Text style={tw`text-[19px] leading-6 font-bold`} numberOfLines={3}>
          {eventTitle(event)}
        </Text>
        {event.event_status === "live" &&
          (event.is_test ? (
            <TestIndicator width={42} height={28} style={tw`-ml-1`} />
          ) : (
            <LiveIndicator width={42} height={28} style={tw`-ml-1`} />
          ))}
        {!!dateLine && (
          <Text style={tw`text-[13px] font-bold text-gray-600 mt-1`}>
            {dateLine}
          </Text>
        )}
        {isGrouped(event) && (
          <RoomSwitcher
            current={event}
            primary={groupPrimary || (isRoom(event) ? null : event)}
            rooms={groupRooms}
            onSelect={onSwitchRoom}
            onAdd={roomsAddable ? onAddRoomPress : undefined}
            renamable={roomsRenamable}
            style={tw`mt-3`}
          />
        )}
      </View>

      <ScrollView contentContainerStyle={tw`px-2 pb-6`}>
        {items.map((item) => {
          const active = !!item.screen && item.screen === activeSection;
          const color = item.destructive || active ? brandColor : "#3F5B7A";
          return (
            <Pressable
              key={item.title}
              accessibilityRole="button"
              accessibilityState={{
                selected: active,
                disabled: !!item.disabled,
              }}
              onPress={
                item.disabled
                  ? undefined
                  : () =>
                      item.onPress ? item.onPress() : onOpen(item.screen, event)
              }
              style={({ hovered, pressed }) => [
                tw`flex-row items-center rounded-lg px-3 py-2.5 my-0.5`,
                item.destructive && tw`mt-4`,
                active
                  ? tw`bg-[#FFEDE9]`
                  : (hovered || pressed) && tw`bg-gray-200/70`,
              ]}
            >
              <Ionicons
                name={item.icon}
                size={20}
                color={color}
                style={tw`mr-3`}
              />
              <Text
                style={[
                  tw`text-[15px] flex-1 text-gray-900`,
                  (active || item.destructive) && { color: brandColor },
                  active && tw`font-semibold`,
                ]}
                numberOfLines={2}
              >
                {item.title}
              </Text>
            </Pressable>
          );
        })}
      </ScrollView>
      {modals}
    </View>
  );
};

export default EventSidebar;
